<script setup lang="ts">
import { computed } from 'vue';
import { Icon } from '@iconify/vue';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';

const languageStore = useLanguageStore();
const { createSyncT } = useTranslation();
const t = createSyncT(languageStore.currentLanguage);

// Props
interface Props {
  modelValue: {
    all: boolean;
    createDocument: boolean;
    requestTSign: boolean;
    proceedWithEFiling: boolean;
  };
  taskName?: string;
  isAllSupported?: boolean;
  isCreateDocumentSupported?: boolean;
  isRequestTSignSupported?: boolean;
  isProceedWithEFilingSupported?: boolean;
  visible?: boolean;
  disableDocDeselect?: boolean; // DOC 해제 방지 (Document Creation 카드 선택 시)
  disableTSignDeselect?: boolean; // T-Sign 해제 방지 (T-Sign 카드 선택 시)
}

const props = withDefaults(defineProps<Props>(), {
  taskName: '',
  isAllSupported: false,
  isCreateDocumentSupported: false,
  isRequestTSignSupported: false,
  isProceedWithEFilingSupported: false,
  visible: true,
  disableDocDeselect: false,
  disableTSignDeselect: false
});

// Emits
const emit = defineEmits<{
  'update:modelValue': [value: Props['modelValue']];
}>();

// 프로세스 목록 정의
const processes = computed(() => [
  {
    key: 'createDocument',
    name: 'Doc',
    icon: 'tabler:file-text',
    supported: props.isCreateDocumentSupported,
    selected: props.modelValue.createDocument
  },
  {
    key: 'requestTSign',
    name: 'T-Sign',
    icon: 'tabler:signature',
    supported: props.isRequestTSignSupported,
    selected: props.modelValue.requestTSign
  },
  {
    key: 'proceedWithEFiling',
    name: 'e-Filing(SG)',
    icon: 'tabler:cloud-upload',
    supported: props.isProceedWithEFilingSupported,
    selected: props.modelValue.proceedWithEFiling
  }
]);

// 프로세스 토글
const toggleProcess = (key: string) => {
  const newValue = { ...props.modelValue };

  if (key === 'createDocument' && props.isCreateDocumentSupported) {
    // DOC 해제 시도 시 Document Creation 카드가 선택되어 있으면 막기
    if (props.modelValue.createDocument && props.disableDocDeselect) {
      console.warn('⚠️ Cannot deselect DOC when Document Creation card is selected');
      return;
    }
    newValue.createDocument = !newValue.createDocument;
  } else if (key === 'requestTSign' && props.isRequestTSignSupported) {
    // T-Sign 해제 시도 시 T-Sign 카드가 선택되어 있으면 막기
    if (props.modelValue.requestTSign && props.disableTSignDeselect) {
      console.warn('⚠️ Cannot deselect T-Sign when T-Sign card is selected');
      return;
    }
    newValue.requestTSign = !newValue.requestTSign;
  } else if (key === 'proceedWithEFiling' && props.isProceedWithEFilingSupported) {
    newValue.proceedWithEFiling = !newValue.proceedWithEFiling;
  }

  // All 체크박스 상태 업데이트
  newValue.all = newValue.createDocument && newValue.requestTSign && newValue.proceedWithEFiling;

  emit('update:modelValue', newValue);
};
</script>

<template>
  <v-slide-y-transition>
    <div v-if="visible" class="task-flow-section mt-6">
      <!-- 헤더 -->
      <div class="task-flow-header mb-4">
        <h2 class="text-h5 font-weight-bold">{{ t('startTask.Task Flow') || 'Task Flow' }}</h2>
        <p v-if="taskName" class="text-body-2 text-medium-emphasis mt-1">{{ taskName }}</p>
      </div>

      <!-- 프로세스 목록 -->
      <v-row>
        <v-col
          cols="12" sm="4" md="4" lg="3"
          v-for="process in processes"
          :key="process.key"
        >
          <v-card
            elevation="0"
            :class="[
              'process-card d-flex align-center py-4 px-5 rounded-lg',
              { 'process-card--active': process.selected },
              { 'process-card--disabled': !process.supported },
              { 'cursor-pointer': process.supported }
            ]"
            @click="process.supported && toggleProcess(process.key)"
          >
            <!-- 체크 아이콘 -->
            <div class="process-check me-3">
              <Icon
                :icon="process.selected ? 'tabler:circle-check-filled' : 'tabler:circle'"
                height="24"
                :class="process.selected ? 'text-success' : 'text-grey'"
              />
            </div>
            <!-- 프로세스 아이콘 -->
            <Icon
              :icon="process.icon"
              height="20"
              class="me-2"
              :class="process.selected ? 'text-success' : 'text-medium-emphasis'"
            />
            <!-- 프로세스 이름 -->
            <span
              class="text-body-1 font-weight-medium"
              :class="process.selected ? 'text-success' : ''"
            >
              {{ process.name }}
            </span>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </v-slide-y-transition>
</template>

<style scoped lang="scss">
/* Task Flow 섹션 (ServiceCategoryManagement 스타일) */
.task-flow-section {
  padding: 24px;
  background: rgb(var(--v-theme-surface));
  border-radius: 16px;
  border: 1px solid rgba(var(--v-border-color), 0.1);
}

.task-flow-header {
  border-bottom: 1px solid rgba(var(--v-border-color), 0.1);
  padding-bottom: 16px;
}

/* 프로세스 카드 */
.process-card {
  background: rgb(var(--v-theme-surface)) !important;
  border: 1.5px solid rgba(var(--v-border-color), 0.12);
  transition: all 0.2s ease;
  white-space: nowrap;
  min-width: fit-content;

  &:hover:not(.process-card--disabled) {
    border-color: rgba(var(--v-theme-success), 0.3);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }

  &--active {
    border-color: rgb(var(--v-theme-success));
    background: rgba(var(--v-theme-success), 0.05) !important;
    box-shadow: 0 4px 12px rgba(var(--v-theme-success), 0.15);
  }

  &--disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.process-check {
  display: flex;
  align-items: center;
  justify-content: center;
}

.cursor-pointer {
  cursor: pointer;
}

/* 반응형 */
@media (max-width: 576px) {
  .task-flow-section {
    padding: 16px;
  }

  .task-flow-header {
    h2 {
      font-size: 18px !important;
    }
  }

  .process-card {
    padding: 12px 16px !important;
  }
}
</style>
